"use client";

import React, { useState, useMemo } from "react";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { MatchList } from "@/components/matches/MatchList";
import { mapMatchToMatchListItem } from "@/utilities/mappers/match";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import { Button } from "@/components/ui/controls/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AdSlot } from "@/features/ads/AdSlot";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import type { ResolvedPromoRow } from "@/features/promo-slots/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useCompetitionSlice } from "@/views/competition-shared/CompetitionSliceProvider";
import type { DerivedRound, MatchRow } from "@/utilities/data/competition/derive/types";
import { resolveRowState } from "./liveRow";
import { matchEventId, useRoundLiveSnapshots } from "./useRoundLiveSnapshots";
import type { LiveEventSnapshot } from "@/features/live-data/snapshots";
interface MatchesListPageClientProps {
  /** Hide the branded header (used when CompetitionBrandedHeader is shown) */
  hideHeader?: boolean;
  /** Resolved header colors from section styles */
  headerColors?: ResolvedHeaderColors;
  /** Optional breadcrumbs element rendered inside the BrandedHeader (comp-site path). */
  breadcrumbs?: React.ReactNode;
  /** Resolved promos for the matches page. */
  promos?: (ResolvedPromoRow | null)[];
  /** Every round of the season in display order, matches sorted by kickoff. */
  rounds: DerivedRound[];
  /** Round shown on first render (active round, else latest with finished matches). */
  initialRoundIndex: number;
}
interface RoundView {
  round: DerivedRound;
  heading: string;
  dateRange: string | null;
  items: ReturnType<typeof mapMatchToMatchListItem>[];
  hasLive: boolean;
  liveMinute: string | null;
  liveScore: string | null;
}
const formatRangeDate = (date: Date) => date.toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "short"
});
function getDateRange(matches: MatchRow[]): string | null {
  if (matches.length === 0) return null;
  const first = formatRangeDate(new Date(matches[0].timing.kickoffTime));
  const last = formatRangeDate(new Date(matches[matches.length - 1].timing.kickoffTime));
  // If same date, show only once
  return first === last ? first : `${first} - ${last}`;
}
type MapperArgs = Parameters<typeof mapMatchToMatchListItem>;
interface RoundViewDeps {
  roundOfLabel: string;
  matchHref: MapperArgs[3];
  resolveLogoUrl: MapperArgs[4];
}
const NO_SNAPSHOTS: ReadonlyMap<string, LiveEventSnapshot> = new Map();
function buildRoundView(round: DerivedRound, snapshots: ReadonlyMap<string, LiveEventSnapshot>, now: Date, deps: RoundViewDeps): RoundView {
  // Numeric names come from league stages ("1", "2", …) — those keep the
  // "Round X of Y" format (total counted within the containing stage).
  // Semantic knockout names ("Round of 16", "Final") render as-is because
  // the "Round X of Y" template doesn't fit them.
  const heading = !/^\d+$/.test(round.name) ? round.name : deps.roundOfLabel.replace("{current}", round.name).replace("{total}", String(round.stageRoundCount));
  let liveMinute: string | null = null;
  let liveScore: string | null = null;
  let hasLive = false;
  const items = round.matches.map(match => {
    const state = resolveRowState(match, snapshots.get(matchEventId(match)), now);
    if (state.live && !hasLive) {
      liveMinute = state.live.minute;
      liveScore = `${state.live.scoreOne ?? "0"}-${state.live.scoreTwo ?? "0"}`;
    }
    hasLive ||= state.live !== undefined;
    return mapMatchToMatchListItem(state.match, state.isFinished, false, deps.matchHref, deps.resolveLogoUrl, state.live);
  });
  return {
    round,
    heading,
    dateRange: getDateRange(round.matches),
    items,
    hasLive,
    liveMinute,
    liveScore
  };
}
export function MatchesListPageClient({
  hideHeader = false,
  headerColors,
  breadcrumbs,
  promos,
  rounds,
  initialRoundIndex
}: MatchesListPageClientProps) {
  const {
    competition,
    seasonName
  } = useCompetitionSlice();
  const labels = useLabels().matches;
  const nav = useNavigation();
  const resolveLogoUrl = useTeamLogoUrl();
  const competitionHref = useMemo(() => {
    if (!competition?.id) return undefined;
    try {
      return nav.footballCompetition(competition.id);
    } catch {
      return undefined;
    }
  }, [competition?.id, nav]);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(initialRoundIndex);
  const currentRound = rounds[currentRoundIndex];

  // Only the displayed round polls; hidden rounds keep their server-rendered state.
  const liveSnapshots = useRoundLiveSnapshots(currentRound?.matches ?? []);
  const roundViewDeps = useMemo<RoundViewDeps>(() => ({
    roundOfLabel: labels.roundOf,
    matchHref: nav.match,
    resolveLogoUrl
  }), [labels.roundOf, nav.match, resolveLogoUrl]);

  // Snapshots only ever cover the displayed round: every round's view is built once from the
  // server data, and only the displayed round's view is rebuilt when a poll lands.
  const roundViews = useMemo<RoundView[]>(() => {
    const now = new Date();
    return rounds.map(round => buildRoundView(round, NO_SNAPSHOTS, now, roundViewDeps));
  }, [rounds, roundViewDeps]);
  const currentView = useMemo<RoundView | undefined>(() => currentRound ? buildRoundView(currentRound, liveSnapshots, new Date(), roundViewDeps) : undefined, [currentRound, liveSnapshots, roundViewDeps]);
  const handlePreviousRound = () => {
    if (currentRoundIndex > 0) {
      setCurrentRoundIndex(currentRoundIndex - 1);
    }
  };
  const handleNextRound = () => {
    if (currentRoundIndex < rounds.length - 1) {
      setCurrentRoundIndex(currentRoundIndex + 1);
    }
  };
  if (rounds.length === 0) {
    return <div className="min-h-screen">
                {!hideHeader && <BrandedHeader title={labels.title} subtitle={`${competition?.name} ${seasonName}`} imageUrl={competition?.assets?.logo} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} href={competitionHref} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}
                <div className="max-w-350 mx-auto px-4 py-8">
                    <div className="text-center text-muted-foreground py-8">{labels.noRoundsAvailable}</div>
                </div>
            </div>;
  }
  const current = currentView ?? roundViews[currentRoundIndex];
  return <div className="min-h-screen">
            {!hideHeader && <BrandedHeader title={labels.title} subtitle={`${competition?.name} ${seasonName}`} imageUrl={competition?.assets?.logo} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}

            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                {/*
                  Every other round is in the HTML for crawlers but hidden. They sit before the displayed round so
                  the displayed round's markup and the container's `space-y` rhythm stay exactly as a single-round
                  page; the displayed round is the one with the navigation row.
                 */}
                {roundViews.map((view, index) => index === currentRoundIndex ? null : <section key={view.round.id} data-round={view.round.id} hidden data-live={view.hasLive ? "1" : undefined} data-live-minute={view.hasLive ? view.liveMinute ?? undefined : undefined} data-live-score={view.hasLive ? view.liveScore ?? undefined : undefined}>
                            <h2 className="text-xl font-semibold">{view.heading}</h2>
                            <p className="text-sm text-muted-foreground mt-0">{view.dateRange}</p>
                            <MatchList matches={view.items} variant="teamFixtures" emptyMessage={labels.noMatchesInRound} active={false} />
                        </section>)}

                {/* Round Navigation Header */}
                <div className="flex items-center justify-between mb-6">
                    <Button variant="outline" size="icon" onClick={handlePreviousRound} disabled={currentRoundIndex === 0} className="h-10 w-10 cursor-pointer">
                        <ChevronLeft className="h-5 w-5" />
                    </Button>

                    <div className="text-center">
                        <h2 className="text-xl font-semibold">{current.heading}</h2>
                        <p className="text-sm text-muted-foreground mt-0">{current.dateRange}</p>
                    </div>

                    <Button variant="outline" size="icon" onClick={handleNextRound} disabled={currentRoundIndex === rounds.length - 1} className="h-10 w-10 cursor-pointer">
                        <ChevronRight className="h-5 w-5" />
                    </Button>
                </div>

                {/* Matches List */}
                <MatchList matches={current.items} variant="teamFixtures" emptyMessage={labels.noMatchesInRound} />

                <PromoSlot row={promos?.[0] ?? null} />

                <AdSlot id="content-bottom" />
            </div>
        </div>;
}